import React, {useEffect, useRef} from 'react'
import AdyenCheckout from '@adyen/adyen-web'
import '@adyen/adyen-web/dist/adyen.css'
import '../../adyen-overrides.css'
import useAdyenCheckout from '../hooks/useAdyenCheckout'
import {setCheckoutBasketRecoveryPending} from '../checkout-basket-recovery'
import {Flex, Spinner} from '@chakra-ui/react'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import PropTypes from 'prop-types'
import useTagManager from '../../../hooks/use-tag-manager'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {pageTypes} from '../../../adyen'

export const getCheckoutConfig = (
    adyenEnvironment,
    adyenPaymentMethods,
    paymentMethodsConfiguration,
    translations,
    locale
) => {
    const checkoutConfig = {
        environment: adyenEnvironment?.ADYEN_ENVIRONMENT,
        clientKey: adyenEnvironment?.ADYEN_CLIENT_KEY,
        paymentMethodsResponse: adyenPaymentMethods,
        paymentMethodsConfiguration: paymentMethodsConfiguration
    }
    if (translations) {
        checkoutConfig.locale = locale.id
        checkoutConfig.translations = translations
    }
    return checkoutConfig
}

export const handleQueryParams = async (
    urlParams,
    checkout,
    paymentContainer,
    dropIn,
    navigate,
    setAdyenSelectedPaymentMethod,
    basket,
    tagManager,
    paymentInfoTimeout,
    setAdyenDropInData
) => {
    const redirectResult = urlParams.get('redirectResult')
    const adyenAction = urlParams.get('adyenAction')

    if (redirectResult) {
        try {
            await checkout.submitDetails({data: {details: {redirectResult}}})
        } catch (error) {
            setCheckoutBasketRecoveryPending()
            navigate('/checkout')
        }
    } else if (adyenAction) {
        const actionString = atob(adyenAction)
        const action = JSON.parse(actionString)
        dropIn.current = checkout.createFromAction(action).mount(paymentContainer.current)
    } else {
        dropIn.current = checkout
            .create('dropin', {
                onSelect(component) {
                    setAdyenSelectedPaymentMethod(component.props.type)
                    window.selectedAdyenProps = component.props
                    clearTimeout(paymentInfoTimeout)

                    paymentInfoTimeout = setTimeout(() => {
                        // We cannot put this in constants.js
                        const PAYMENT_TYPES_MAPPING = {
                            card: 'credit_card',
                            paypal: 'paypal',
                            applepay: 'applepay',
                            klarna: 'pay_later_klarna',
                            klarna_account: 'pay_over_time_klarna',
                            affirm: 'affirm',
                            afterpaytouch_US: 'afterpay',
                            cashapp: 'cashapppay'
                        }
                        try {
                            tagManager.addPaymentInfo(
                                basket,
                                window.pwaProducts,
                                basket?.couponItems?.map((c) => c.code).join(',') || '',
                                PAYMENT_TYPES_MAPPING?.[component.props.type]
                            )
                            localStorage.setItem(
                                'selectedPaymentMethod',
                                PAYMENT_TYPES_MAPPING?.[component.props.type]
                            )
                            const giftCertCount = Number(localStorage.getItem('giftCertCount')) || 0
                            const paymentMethods = [
                                ...Array(giftCertCount).fill('gift_certificate'),
                                PAYMENT_TYPES_MAPPING?.[component.props.type]
                            ].join(' | ')

                            tagManager.paymentMethodSelection(paymentMethods)
                        } catch (error) {
                            console.error(
                                'Error sending payment info to tag manager',
                                error.message
                            )
                        }
                    }, 500)
                }
            })
            .mount(paymentContainer.current)
        setAdyenDropInData(dropIn.current)
    }
}

const AdyenCheckoutComponent = (props) => {
    const {
        adyenEnvironment,
        adyenPaymentMethods,
        orderNo,
        getPaymentMethodsConfiguration,
        setAdyenStateData,
        setOrderNo,
        getTranslations,
        locale,
        adyenPaymentInProgress,
        navigate,
        setAdyenSelectedPaymentMethod,
        page
    } = useAdyenCheckout()
    const {setAdyenDropInData} = props
    const paymentContainer = useRef(null)
    const paymentInfoTimeout = useRef(null)
    const tagManager = useTagManager()
    const {data: basket} = useCurrentBasket()
    const dropIn = useRef(null)
    const paypalSDKScript = onClient()
        ? document.querySelector('script[src*="paypal.com/sdk"]')
        : null

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search)
        let isMounted = true

        const createCheckout = async () => {
            const paymentMethodsConfiguration = await getPaymentMethodsConfiguration(props)
            const translations = getTranslations()
            const checkoutConfig = getCheckoutConfig(
                adyenEnvironment,
                adyenPaymentMethods,
                paymentMethodsConfiguration,
                translations,
                locale
            )
            const checkout = await AdyenCheckout({
                ...checkoutConfig,
                beforeSubmit: async (data, component, actions) => {
                    if (props.validateBillingAddress) {
                        const isValid = await props.validateBillingAddress()
                        if (!isValid) {
                            actions.reject()
                            return
                        }
                    }
                    actions.resolve(data)
                },
                onSubmit: async (state, element) => {
                    const onSubmit =
                        paymentMethodsConfiguration.onSubmit ||
                        paymentMethodsConfiguration.card.onSubmit
                    const result = await onSubmit(state, element)
                    if (!result?.paymentsResponse) {
                        return
                    }
                    setOrderNo(result.paymentsResponse.merchantReference)
                },
                async onAdditionalDetails(state, element) {
                    const onAdditionalDetails =
                        paymentMethodsConfiguration.onAdditionalDetails ||
                        paymentMethodsConfiguration.card.onAdditionalDetails
                    await onAdditionalDetails(state, element)
                },
                onChange: (state) => {
                    if (state.isValid) {
                        setAdyenStateData(state.data)
                    }
                },
                onError: async (error) => {
                    const onError =
                        paymentMethodsConfiguration.onError ||
                        paymentMethodsConfiguration.card.onError
                    await onError(error, orderNo, navigate)

                    // If redirect details fail/cancel, allow basket restore to complete first.
                    if (window?.location?.pathname?.includes('/checkout/redirect')) {
                        setCheckoutBasketRecoveryPending()
                        navigate('/checkout')
                    }
                }
            })

            // Break early if the component unmounted while AdyenCheckout resolved
            if (!isMounted) return

            await handleQueryParams(
                urlParams,
                checkout,
                paymentContainer,
                dropIn,
                navigate,
                setAdyenSelectedPaymentMethod,
                basket,
                tagManager,
                paymentInfoTimeout,
                setAdyenDropInData
            )
        }

        if (
            adyenEnvironment &&
            paymentContainer.current &&
            (page === pageTypes.CHECKOUT || page === pageTypes.REDIRECT) &&
            !adyenPaymentInProgress &&
            props.updateAdyenCheckout !== false
        ) {
            if (window?.paypal?.firstElementChild && !paypalSDKScript) {
                window.paypal = undefined
            }
            createCheckout()
        }

        // React cleanup function
        return () => {
            isMounted = false // Stop active async operations

            if (dropIn.current) {
                dropIn.current?.unmount()
                dropIn.current = null
            }

            if (paymentContainer.current) {
                paymentContainer.current.innerHTML = ''
            }
        }
    }, [adyenEnvironment, adyenPaymentMethods, props.updateAdyenCheckout])

    return (
        <>
            {props.showLoading && (
                <Flex align={'center'} justify={'center'}>
                    <Spinner size={'lg'} mt={4} />
                </Flex>
            )}
            <div className="payment-container" ref={paymentContainer}></div>
        </>
    )
}

AdyenCheckoutComponent.propTypes = {
    showLoading: PropTypes.bool,
    setAdyenDropInData: PropTypes.any,
    updateAdyenCheckout: PropTypes.bool,
    validateBillingAddress: PropTypes.func
}

export default AdyenCheckoutComponent
